import React, { useEffect, useState } from "react";
import { Contact, MessageSquare, Mail, Copy, Check, UserPlus } from "lucide-react";
import Modal from "@/components/chat/Modal";
import api from "@/lib/api";
import { getInitials, avatarColor } from "@/lib/avatar";
import { hasNativeBridge, getDeviceType } from "@/lib/device";
import { toast } from "sonner";

export default function InviteDialog({ onClose, onStartChat }) {
  const [message, setMessage] = useState("Join me on RoyalChat Nova: https://royalchat.online");
  const [registered, setRegistered] = useState([]);
  const [toInvite, setToInvite] = useState([]); // {name, tel}
  const [manual, setManual] = useState("");
  const [scanned, setScanned] = useState(false);
  const [busy, setBusy] = useState(false);
  // Two ways to read contacts:
  //  1) Median (GoNative) Android app → native bridge `median.contacts.getAll()` (requires the
  //     Native Contacts plugin enabled in Median App Studio).
  //  2) Regular Android Chrome (top-level, not an iframe) → the web Contact Picker API.
  // iOS/desktop/iframes have neither → users invite manually.
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const isNativeAndroid = hasNativeBridge() && getDeviceType() === "android";
  const webPicker = typeof navigator !== "undefined" && "contacts" in navigator
    && navigator.contacts && "select" in navigator.contacts && !inIframe;
  const supportsContacts = isNativeAndroid || webPicker;

  useEffect(() => { api.get("/invite-message").then(({ data }) => setMessage(data.message)).catch(() => {}); }, []);

  // Median native contacts → normalized [{name, tel}].
  const pickFromMedian = async () => {
    const median = window.median || window.gonative;
    if (!median?.contacts?.getAll) {
      toast.error("Contacts plugin not enabled in the app yet.");
      return [];
    }
    const result = await median.contacts.getAll({});
    const list = (result && (result.contacts || result.data)) || (Array.isArray(result) ? result : []);
    const out = [];
    for (const c of list) {
      const nums = c.phoneNumbers || c.phones || [];
      for (const p of nums) {
        const tel = typeof p === "string" ? p : (p.value || p.number || p.phoneNumber || "");
        if (tel) {
          const name = [c.givenName, c.familyName].filter(Boolean).join(" ") || c.name || c.displayName || "Contact";
          out.push({ name, tel });
          break;
        }
      }
    }
    return out;
  };

  const pickContacts = async () => {
    setBusy(true);
    try {
      let contacts;
      if (isNativeAndroid) {
        contacts = await pickFromMedian();
      } else {
        const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
        if (!picked || picked.length === 0) return; // user dismissed
        contacts = picked.map((c) => ({ name: (c.name && c.name[0]) || "Contact", tel: (c.tel && c.tel[0]) || "" }));
      }
      contacts = (contacts || []).filter((c) => c.tel);
      if (contacts.length === 0) { toast("No phone numbers found in your contacts"); return; }
      const { data } = await api.post("/users/match", { phones: contacts.map((c) => c.tel) });
      const norm = (p) => (p || "").replace(/\D/g, "").slice(-9);
      const regPhones = new Set(data.registered.map((r) => norm(r.phone)));
      setRegistered(data.registered);
      setToInvite(contacts.filter((c) => !regPhones.has(norm(c.tel))));
      setScanned(true);
    } catch (e) {
      if (e && e.name === "AbortError") return; // web picker dismissed
      toast.error("Couldn't read contacts — please invite manually below.");
    } finally { setBusy(false); }
  };

  const smsInvite = (tel) => { window.location.href = `sms:${tel || ""}?&body=${encodeURIComponent(message)}`; };
  const emailInvite = (email) => { window.location.href = `mailto:${email || ""}?subject=${encodeURIComponent("Join me on RoyalChat Nova")}&body=${encodeURIComponent(message)}`; };
  const copyLink = () => { navigator.clipboard.writeText(message); toast.success("Invite copied"); };

  return (
    <Modal title="Invite friends" onClose={onClose}>
      <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-3 text-sm text-[var(--rc-muted)] mb-4">{message}</div>

      {supportsContacts ? (
        <button onClick={pickContacts} disabled={busy} data-testid="pick-contacts-btn" className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3 mb-4 disabled:opacity-60">
          <Contact size={18} /> {busy ? "Reading contacts…" : "Pick from my contacts"}
        </button>
      ) : (
        <p className="text-xs text-[var(--rc-muted)] mb-3" data-testid="contacts-unavailable-note">
          Picking from your contacts works in the RoyalChat Nova app on Android. Here, just invite people manually below.
        </p>
      )}

      {registered.length > 0 && (
        <>
          <p className="text-xs font-semibold tracking-wider text-[var(--rc-gold)] mb-2">ALREADY ON ROYALCHAT</p>
          <div className="space-y-1 mb-4">
            {registered.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-2 rounded-xl" data-testid={`reg-contact-${r.id}`}>
                <div className="h-9 w-9 rounded-full grid place-items-center text-[var(--rc-gold)] text-xs font-semibold border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(r.name) }}>{getInitials(r.name)}</div>
                <span className="flex-1 text-sm truncate">{r.name}<span className="ml-2 text-[10px] text-emerald-400">● on app</span></span>
                {onStartChat && <button onClick={() => onStartChat(r.id)} className="text-sm text-[var(--rc-gold)] flex items-center gap-1"><MessageSquare size={15} /> Chat</button>}
              </div>
            ))}
          </div>
        </>
      )}

      {toInvite.length > 0 && (
        <>
          <p className="text-xs font-semibold tracking-wider text-[var(--rc-muted)] mb-2">INVITE THESE CONTACTS</p>
          <div className="space-y-1 mb-4">
            {toInvite.map((c, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-xl" data-testid={`invite-contact-${i}`}>
                <div className="h-9 w-9 rounded-full grid place-items-center bg-[var(--rc-surface-2)] text-[var(--rc-muted)]"><UserPlus size={16} /></div>
                <span className="flex-1 min-w-0 text-sm truncate">{c.name}<span className="block text-[10px] text-[var(--rc-muted)]">{c.tel}</span></span>
                <button onClick={() => smsInvite(c.tel)} className="h-9 w-9 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-gold)]"><MessageSquare size={16} /></button>
                <button onClick={() => emailInvite("")} className="h-9 w-9 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-gold)]"><Mail size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* manual invite */}
      <p className="text-xs font-semibold tracking-wider text-[var(--rc-muted)] mb-2">INVITE MANUALLY</p>
      <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Phone number or email" data-testid="invite-manual-input"
        className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 mb-2 focus:outline-none focus:border-[var(--rc-gold)]/50" />
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => smsInvite(manual)} data-testid="invite-sms-btn" className="flex items-center justify-center gap-1.5 rounded-xl border border-[var(--rc-border)] py-2.5 text-sm"><MessageSquare size={15} /> SMS</button>
        <button onClick={() => emailInvite(manual)} data-testid="invite-email-btn" className="flex items-center justify-center gap-1.5 rounded-xl border border-[var(--rc-border)] py-2.5 text-sm"><Mail size={15} /> Email</button>
        <button onClick={copyLink} data-testid="invite-copy-btn" className="flex items-center justify-center gap-1.5 rounded-xl border border-[var(--rc-border)] py-2.5 text-sm"><Copy size={15} /> Copy</button>
      </div>
    </Modal>
  );
}
